import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { RollsetInfo } from "./RollsetDeactivationConfirmModal";

export type CarrierInfo = {
	CarrierName: string | null;
	CarrierStatusName: string | null;
};

export type ActiveCarrierAction = "delete" | "status";

type Props = {
	show: boolean;
	action?: ActiveCarrierAction;
	entityName: string;
	entityType: string;
	actionDescription: string;
	carrier?: CarrierInfo | null;
	rollsetInfo?: RollsetInfo | null;
	onClose: () => void;
};

const StatBox = ({
	label,
	value,
	centered = true,
}: {
	label: string;
	value: string | null;
	centered?: boolean;
}) => (
	<Box sx={{ textAlign: centered ? "center" : "left", flex: 1 }}>
		<Typography variant="subtitle1" fontWeight={600}>
			{value || "N/A"}
		</Typography>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
	</Box>
);

const RollCard = ({
	label,
	rollName,
}: {
	label: string;
	rollName: string | null;
}) => (
	<Paper
		variant="outlined"
		sx={{
			flex: 1,
			p: 1.5,
			border: "none",
		}}
	>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="subtitle1" fontWeight={600}>
			{rollName || "N/A"}
		</Typography>
	</Paper>
);

export const ActiveCarrierModal = ({
	show,
	action = "delete",
	entityName,
	entityType,
	actionDescription,
	carrier,
	rollsetInfo,
	onClose,
}: Props) => {
	const titleText =
		action === "delete" ? "Cannot Delete Part" : "Cannot change status of Part";

	return (
		<Dialog open={show} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="center" gap={1}>
					<BlockIcon color="error" />
					<Typography variant="h6" component="span">
						{titleText}
					</Typography>
				</Stack>
			</DialogTitle>

			<IconButton
				size="small"
				onClick={onClose}
				aria-label="close"
				sx={{ position: "absolute", top: 12, right: 12 }}
			>
				<CloseIcon fontSize="small" />
			</IconButton>

			<DialogContent dividers>
				<Stack spacing={2}>
					{entityName && (
						<Typography variant="body2" color="text.secondary">
							<strong>{entityName}</strong> ({entityType})
						</Typography>
					)}

					<Typography variant="body2" color="text.secondary">
						{actionDescription}
					</Typography>

					{carrier && (
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								bgcolor: "grey.50",
								borderColor: "error.main",
								borderWidth: 2,
								borderRadius: 2,
							}}
						>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ mb: 2 }}
							>
								<Typography variant="subtitle2" fontWeight={600}>
									Carrier
								</Typography>
							</Stack>

							<Paper
								sx={{
									display: "flex",
									border: "none",
									p: 1.5,
									borderRadius: 1,
								}}
								elevation={0}
							>
								<StatBox label="Carrier" value={carrier.CarrierName} />
								<StatBox label="Status" value={carrier.CarrierStatusName} />
							</Paper>
						</Paper>
					)}

					{rollsetInfo && (
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								bgcolor: "grey.50",
								borderColor: "error.main",
								borderWidth: 2,
								borderRadius: 2,
							}}
						>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ mb: 2 }}
							>
								<Typography variant="subtitle2" fontWeight={600}>
									Rollset
								</Typography>
							</Stack>

							<Paper
								sx={{
									display: "flex",
									border: "none",
									p: 1.5,
									mb: 2,
									borderRadius: 1,
								}}
								elevation={0}
							>
								<StatBox
									label="Section"
									value={rollsetInfo.RollsetSectionName}
								/>
								<StatBox label="Stand" value={rollsetInfo.RollsetStandName} />
								<StatBox label="Letter" value={rollsetInfo.RollsetLetter} />
							</Paper>

							<Stack direction="row" spacing={1.5}>
								<RollCard
									label="Top Roll"
									rollName={rollsetInfo.RollsetTopRollName}
								/>
								<RollCard
									label="Bottom Roll"
									rollName={rollsetInfo.RollsetBottomRollName}
								/>
							</Stack>
						</Paper>
					)}
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button variant="contained" color="error" onClick={onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};
